<template>
  <div class="mb-6">
    <div v-if="message.role === 'user'" class="chat-bubble user-message">
      <div class="message-meta">{{ formattedTimestamp }}</div>
      <div class="message-text">{{ displayContent }}</div>
    </div>
    <div v-else-if="message.role === 'assistant'" class="chat-bubble system-message">
      <div class="message-meta">{{ formattedTimestamp }}</div>
      <div class="message-text" v-html="parsedContent" />
      <span v-if="hasViewData">
        <v-btn class="mt-2 mb-2 mr-3" small color="primary" @click="$emit('view-data')">
          View Data
        </v-btn>
      </span>
      <div v-if="fileAttachment">
        <v-btn
          class="mt-2 mb-2"
          small
          outlined
          color="white"
          :loading="downloading"
          @click="onDownload"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
        <div v-if="downloadError" class="download-error">{{ downloadError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import { downloadTraceyFile } from '../APIManager';

/** True if cell has no meaningful content (ignores whitespace/nbsp). */
function isCellEmpty(cell) {
  if (!cell) return true;
  const text = (cell.textContent || '').replace(/\s/g, '');
  return text.length === 0;
}

/** Hide columns that have no content in any data row (header row is ignored). */
function hideEmptyTableColumns(html) {
  if (!html || typeof html !== 'string') return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const tables = doc.querySelectorAll('table');
  tables.forEach((table) => {
    const wrap = doc.createElement('div');
    wrap.className = 'message-text-table-wrap';
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);

    const rows = table.querySelectorAll('tr');
    const numCols = rows[0] ? (rows[0].querySelectorAll('th, td').length) : 0;
    if (numCols === 0) return;
    const emptyColIndices = [];
    for (let c = 0; c < numCols; c++) {
      let allDataRowsEmpty = true;
      for (let r = 1; r < rows.length; r++) {
        const cells = rows[r].querySelectorAll('th, td');
        const cell = cells[c];
        if (!isCellEmpty(cell)) {
          allDataRowsEmpty = false;
          break;
        }
      }
      if (allDataRowsEmpty) emptyColIndices.push(c + 1);
    }
    emptyColIndices.forEach((oneBased) => {
      table.classList.add('hide-col-' + oneBased);
    });
  });
  return doc.body.innerHTML;
}

export default {
  name: 'ChatMessageBubble',
  props: {
    message: {
      type: Object,
      required: true,
      validator: (m) => ['user', 'assistant'].includes(m.role),
    },
    isQueryDataPresent: {
      type: Function,
      required: true,
    },
    formatTimestamp: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      downloading: false,
      downloadError: '',
    };
  },
  methods: {
    async onDownload() {
      const attachment = this.fileAttachment;
      if (!attachment || this.downloading) return;
      this.downloading = true;
      this.downloadError = '';
      try {
        await downloadTraceyFile(attachment.downloadUrl, attachment.filename);
      } catch (e) {
        this.downloadError = 'That download link has expired. Ask Tracey to regenerate the PDF.';
        console.error('Tracey PDF download failed:', e);
      } finally {
        this.downloading = false;
      }
    },
  },
  computed: {
    displayContent() {
      return this.message.content ?? this.message.userInput ?? '';
    },
    parsedContent() {
      const content = this.message.content ?? this.message.response ?? '';
      if (!content) return '';
      const raw = marked.parse(content);
      return hideEmptyTableColumns(raw);
    },
    formattedTimestamp() {
      return this.formatTimestamp(this.message.createdAt);
    },
    hasViewData() {
      return this.isQueryDataPresent(this.message);
    },
    fileAttachment() {
      return this.message.fileAttachment || null;
    },
  },
};
</script>

<style scoped>
.chat-bubble {
  position: relative;
  max-width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.system-message {
  background-color: #12253a;
  color: white;
  margin-right: auto;
  margin-left: 0;
  border-radius: 18px 18px 18px 4px;
  margin-top: 14px;
}

.system-message::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 0;
  border: 8px solid transparent;
  border-right-color: #12253a;
  border-left: 0;
  border-bottom: 0;
  margin-left: -6px;
  margin-bottom: 12px;
}

.user-message {
  background-color: #e6f0f5;
  color: #12253a;
  margin-left: auto;
  margin-right: 0;
  border-radius: 18px 18px 4px 18px;
  border: 1px solid rgba(18, 37, 58, 0.1);
  margin-top: 8px;
}

.user-message::before {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  width: 0;
  height: 0;
  border: 8px solid transparent;
  border-left-color: #e6f0f5;
  border-right: 0;
  border-bottom: 0;
  margin-right: -6px;
  margin-bottom: 12px;
}

.message-meta {
  font-size: 10px;
  color: #6b7280;
}

.download-error {
  font-size: 11px;
  color: #ffb4b4;
  margin-bottom: 6px;
}

::v-deep .system-message .message-text a {
  color: var(--v-highlight-lighten2) !important;
  text-decoration: underline !important;
}

/* Scrollable wrapper: fixed width so table can overflow and scroll horizontally */
::v-deep .message-text .message-text-table-wrap {
  overflow-x: auto;
  margin: 0.5em 0;
  max-width: 100%;
  width: 100%;
}

/* Table grows to content width so it overflows the wrapper when wide */
::v-deep .message-text table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
}

::v-deep .message-text th,
::v-deep .message-text td {
  padding: 6px 10px;
  max-width: 300px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: left;
}

::v-deep .message-text .message-text-table-wrap th {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Alternating row shades that blend with the bubble background (#12253a) */
::v-deep .message-text .message-text-table-wrap tr:nth-child(odd) td {
  background-color: rgba(255, 255, 255, 0.05);
}

::v-deep .message-text .message-text-table-wrap tr:nth-child(even) td {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Hide empty table columns (classes added by hideEmptyTableColumns) */
::v-deep .message-text table.hide-col-1 th:nth-child(1),
::v-deep .message-text table.hide-col-1 td:nth-child(1) {
  display: none;
}

::v-deep .message-text table.hide-col-2 th:nth-child(2),
::v-deep .message-text table.hide-col-2 td:nth-child(2) {
  display: none;
}

::v-deep .message-text table.hide-col-3 th:nth-child(3),
::v-deep .message-text table.hide-col-3 td:nth-child(3) {
  display: none;
}

::v-deep .message-text table.hide-col-4 th:nth-child(4),
::v-deep .message-text table.hide-col-4 td:nth-child(4) {
  display: none;
}

::v-deep .message-text table.hide-col-5 th:nth-child(5),
::v-deep .message-text table.hide-col-5 td:nth-child(5) {
  display: none;
}

::v-deep .message-text table.hide-col-6 th:nth-child(6),
::v-deep .message-text table.hide-col-6 td:nth-child(6) {
  display: none;
}

::v-deep .message-text table.hide-col-7 th:nth-child(7),
::v-deep .message-text table.hide-col-7 td:nth-child(7) {
  display: none;
}

::v-deep .message-text table.hide-col-8 th:nth-child(8),
::v-deep .message-text table.hide-col-8 td:nth-child(8) {
  display: none;
}

::v-deep .message-text table.hide-col-9 th:nth-child(9),
::v-deep .message-text table.hide-col-9 td:nth-child(9) {
  display: none;
}

::v-deep .message-text table.hide-col-10 th:nth-child(10),
::v-deep .message-text table.hide-col-10 td:nth-child(10) {
  display: none;
}

::v-deep .message-text table.hide-col-11 th:nth-child(11),
::v-deep .message-text table.hide-col-11 td:nth-child(11) {
  display: none;
}

::v-deep .message-text table.hide-col-12 th:nth-child(12),
::v-deep .message-text table.hide-col-12 td:nth-child(12) {
  display: none;
}

::v-deep .message-text table.hide-col-13 th:nth-child(13),
::v-deep .message-text table.hide-col-13 td:nth-child(13) {
  display: none;
}

::v-deep .message-text table.hide-col-14 th:nth-child(14),
::v-deep .message-text table.hide-col-14 td:nth-child(14) {
  display: none;
}

::v-deep .message-text table.hide-col-15 th:nth-child(15),
::v-deep .message-text table.hide-col-15 td:nth-child(15) {
  display: none;
}

::v-deep .message-text table.hide-col-16 th:nth-child(16),
::v-deep .message-text table.hide-col-16 td:nth-child(16) {
  display: none;
}

::v-deep .message-text table.hide-col-17 th:nth-child(17),
::v-deep .message-text table.hide-col-17 td:nth-child(17) {
  display: none;
}

::v-deep .message-text table.hide-col-18 th:nth-child(18),
::v-deep .message-text table.hide-col-18 td:nth-child(18) {
  display: none;
}

::v-deep .message-text table.hide-col-19 th:nth-child(19),
::v-deep .message-text table.hide-col-19 td:nth-child(19) {
  display: none;
}

::v-deep .message-text table.hide-col-20 th:nth-child(20),
::v-deep .message-text table.hide-col-20 td:nth-child(20) {
  display: none;
}
</style>
